import { useCallback, useContext } from "react";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import {
  AUTH_CHECK_BEGIN,
  AUTH_CHECK_SUCCESS,
  AUTH_CHECK_FAILURE,
  INIT_SOCKET,
  LOG_OUT,
  LOG_OUT_START,
  GET_AUTH_INFO_BEGIN,
  GET_AUTH_INFO_SUCCESS,
  GET_AUTH_INFO_FAILURE,
} from "./constants";
import _ from "lodash";
import axios from "axios";
import io from "socket.io-client";
import { apiUrl, socketIOUrl } from "features/configure";
import { enqueueSnackbar } from "features/common/redux/actions";
import InterceptorContext from "features/home/InterceptorContext";
axios.defaults.withCredentials = true;
const initialState = {
  address: "",
  connected: false,
  data: {},
  networkId: Number(process.env.REACT_APP_NETWORK_ID),
};

export function authCheck(
  entityName,
  setRequestAuthPending,
  setRequestAuthDone
) {
  return async (dispatch, getState) => {
    dispatch({
      type: AUTH_CHECK_BEGIN,
    });
    setRequestAuthPending(true);

    try {
      if (!entityName) return;

      const res = await axios.get(
        `${apiUrl}/api/session/authCheck?entityName=` + entityName
      );

      dispatch({
        type: AUTH_CHECK_SUCCESS,
        authorized: res.data.authorized,
      });
      const state = getState();
      initSocket(dispatch, entityName, state.home.wallet.socket);
      setRequestAuthDone(true);
    } catch (err) {
      dispatch({
        type: AUTH_CHECK_FAILURE,
      });
      setRequestAuthDone(false);
      // console.log(err);
    } finally {
      setRequestAuthPending(false);
    }
  };
}

export function getAuthenticatedInfo(entityName) {
  return async (dispatch, getState) => {
    dispatch({
      type: GET_AUTH_INFO_BEGIN,
    });

    try {
      if (!entityName) return;

      const res = await axios.get(
        `${apiUrl}/api/session/admin/info?entityName=` + entityName
      );

      dispatch({
        type: GET_AUTH_INFO_SUCCESS,
        userData: res.data,
      });
      const state = getState();
      initSocket(dispatch, entityName, state.home.wallet.socket);
    } catch (err) {
      dispatch({
        type: GET_AUTH_INFO_FAILURE,
      });
      // console.log(err);
    }
  };
}

const createNewConnection = (dispatch, entityName) => {
  const socket = io(socketIOUrl + "/" + entityName.toLowerCase(), {
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 5000,
    timeout: 20000,
    query: { t: Date.now() },
  });

  socket.on("disconnect", (reason) => {
    console.log("Disconnect Socket:", reason);

    if (reason === "transport close" || reason === "transport error") {
      console.log("server reset");

      setTimeout(() => {
        socket.close();
        const newSocket = createNewConnection(dispatch, entityName);
        dispatch({
          type: INIT_SOCKET,
          socket: newSocket,
        });
      }, 10000);
    }
  });

  socket.on("connect", () => {
    console.log("Socket Connect Success");
    const userId = "admin";
    socket.emit("init", { userId });
  });

  dispatch({
    type: INIT_SOCKET,
    socket: socket,
  });

  return socket;
};

const initSocket = (dispatch, entityName, nowSocket) => {
  if (nowSocket) {
    if (!nowSocket.connected) {
      console.log("Socket down, reconnect");
      nowSocket.close();
      return createNewConnection(dispatch, entityName);
    }
    return nowSocket;
  }

  return createNewConnection(dispatch, entityName);
};

export const signoutAction = async () => {
  if (axios.defaults.headers.common["Authorization"]) {
    await axios.get(`${apiUrl}/api/session/sign-out`);
  }
  localStorage.removeItem("access_token");
  localStorage.removeItem("sessionId");
  delete axios.defaults.headers.common["Authorization"];
};

export function signout(disconnect) {
  return async (dispatch) => {
    try {
      dispatch({ type: LOG_OUT_START });
      await signoutAction();
      disconnect();
      dispatch({ type: LOG_OUT });
    } catch (err) {
      console.log("err: ", err);
    }
  };
}

export function useConnectWallet() {
  const dispatch = useDispatch();
  const {
    userData,
    auth,
    checkAuth,
    socket,
    logoutLoading,
    getAuthenticatedInfoPending,
    getAuthenticatedInfoDone,
  } = useSelector(
    (state) => ({
      userData: state.home.wallet.userData,
      auth: state.home.wallet.auth,
      checkAuth: state.home.wallet.checkAuth,
      socket: state.home.wallet.socket,
      logoutLoading: state.home.wallet.logoutLoading,
      getAuthenticatedInfoPending:
        state.home.wallet.getAuthenticatedInfoPending,
      getAuthenticatedInfoDone: state.home.wallet.getAuthenticatedInfoDone,
    }),
    shallowEqual
  );
  const { setRequestAuthPending, setRequestAuthDone } =
    useContext(InterceptorContext);
  const authAction = useCallback(
    (data) =>
      dispatch(authCheck(data, setRequestAuthPending, setRequestAuthDone)),
    [dispatch]
  );

  const getAuthenticatedInfoAction = useCallback(
    (data) => dispatch(getAuthenticatedInfo(data)),
    [dispatch]
  );

  const signoutAction = useCallback(
    (disconnect) => dispatch(signout(disconnect)),
    [dispatch]
  );

  return {
    userData,
    auth,
    checkAuth,
    logoutLoading,
    socket,
    getAuthenticatedInfoPending,
    getAuthenticatedInfoDone,
    authCheck: authAction,
    getAuthenticatedInfo: getAuthenticatedInfoAction,
    signout: signoutAction,
  };
}

export function reducer(state = initialState, action) {
  switch (action.type) {
    case AUTH_CHECK_BEGIN:
      return {
        ...state,
      };
    case AUTH_CHECK_SUCCESS:
      return {
        ...state,
        auth: action.authorized,
        checkAuth: true,
      };
    case AUTH_CHECK_FAILURE:
      return {
        ...state,
        auth: false,
        checkAuth: true,
      };
    case GET_AUTH_INFO_BEGIN:
      return {
        ...state,
        getAuthenticatedInfoPending: true,
      };
    case GET_AUTH_INFO_SUCCESS:
      const newData = {
        ...state,
        getAuthenticatedInfoDone: true,
        getAuthenticatedInfoPending: false,
      };

      newData.userData = action.userData;

      return newData;

    case GET_AUTH_INFO_FAILURE:
      return {
        ...state,
        userData: undefined,
        getAuthenticatedInfoPending: false,
      };
    case INIT_SOCKET:
      return {
        ...state,
        socket: action.socket,
      };
    case LOG_OUT_START:
      return {
        ...state,
        logoutLoading: true,
      };
    case LOG_OUT:
      return {
        ...state,
        userData: undefined,
        auth: false,
        checkAuth: false,
        logoutLoading: false,
        getAuthenticatedInfoPending: false,
        getAuthenticatedInfoDone: false,
      };

    default:
      return state;
  }
}
